// Branch-level parsers for `@if` + `elseif/else` control-flow chains.
// These helpers parse branch tokens and block boundaries.
import type { IfBranch } from './types'
import { findMatchingPair, isWhitespace, skipWhitespace } from './scanner-utils'

// Parse one `@if(...)` or `elseif(...)` branch.
export function parseConditionalBranch(
  source: string,
  startIndex: number,
  branchType: 'if' | 'elseif',
): { branch: IfBranch; endIndex: number } | null {
  const keywordCandidates = branchType === 'if' ? ['@if'] : ['elseif', '@elseif']
  const keyword = matchBranchKeyword(source, startIndex, keywordCandidates)
  // Reject tokens that do not match current branch keywords.
  if (!keyword) {
    return null
  } else {
    // Continue branch condition parsing.
  }

  let cursor = skipWhitespace(source, startIndex + keyword.length)
  // Reject branch tokens that do not open a condition parenthesis.
  if (source[cursor] !== '(') {
    return null
  } else {
    // Continue conditional-expression parsing.
  }

  const conditionEnd = findMatchingPair(source, cursor, '(', ')')
  // Reject branches with unbalanced condition parentheses.
  if (conditionEnd === -1) {
    return null
  } else {
    // Continue branch body parsing.
  }

  const condition = source.slice(cursor + 1, conditionEnd).trim()
  cursor = skipWhitespace(source, conditionEnd + 1)
  // Reject branch tokens that do not open body with `{`.
  if (source[cursor] !== '{') {
    return null
  } else {
    // Continue branch body block parsing.
  }

  const bodyEnd = findMatchingPair(source, cursor, '{', '}')
  // Reject branches with unbalanced body braces.
  if (bodyEnd === -1) {
    return null
  } else {
    // Return parsed conditional branch payload.
    return {
      branch: { type: branchType, condition, body: source.slice(cursor + 1, bodyEnd) },
      endIndex: bodyEnd + 1,
    }
  }
}

// Parse one optional `else { ... }` branch.
export function parseElseBranch(source: string, startIndex: number): { branch: IfBranch; endIndex: number } | null {
  const keyword = matchBranchKeyword(source, startIndex, ['else', '@else'])
  // Reject tokens that do not match `else` keyword forms.
  if (!keyword) {
    return null
  } else {
    // Continue `else` body parsing.
  }

  let cursor = skipWhitespace(source, startIndex + keyword.length)
  // Reject `else` branches that do not open body with `{`.
  if (source[cursor] !== '{') {
    return null
  } else {
    // Continue `else` body block parsing.
  }

  const bodyEnd = findMatchingPair(source, cursor, '{', '}')
  // Reject `else` branches with unbalanced braces.
  if (bodyEnd === -1) {
    return null
  } else {
    // Return parsed `else` branch payload.
    return {
      branch: { type: 'else', condition: '', body: source.slice(cursor + 1, bodyEnd) },
      endIndex: bodyEnd + 1,
    }
  }
}

// Check whether scanner position starts with a valid `@if` token.
export function matchIfTokenAt(source: string, index: number): boolean {
  // Reject positions that do not start with `@if`.
  if (source.slice(index, index + 3) !== '@if') {
    return false
  } else {
    // Continue token boundary checks.
  }

  const before = index > 0 ? source[index - 1] : ''
  const after = source[index + 3] ?? ''
  // Reject matches that appear inside identifiers.
  if (/[a-zA-Z0-9_-]/.test(before)) {
    return false
  } else if (!isWhitespace(after) && after !== '(') {
    // Reject tokens without valid suffix delimiter.
    return false
  } else {
    // Return true for valid `@if` token boundaries.
    return true
  }
}

// Match one branch keyword with boundary checks and return matched token.
function matchBranchKeyword(source: string, startIndex: number, keywords: string[]): string | null {
  // Return first boundary-valid keyword candidate.
  for (const keyword of keywords) {
    if (source.slice(startIndex, startIndex + keyword.length) !== keyword) {
      continue
    } else {
      // Continue boundary checks for matched keyword text.
    }

    const before = startIndex > 0 ? source[startIndex - 1] : ''
    const after = source[startIndex + keyword.length] ?? ''
    if (/[a-zA-Z0-9_-]/.test(before)) {
      continue
    } else if (!isWhitespace(after) && after !== '(' && after !== '{') {
      continue
    } else {
      // Return keyword when token boundaries are valid.
      return keyword
    }
  }

  // Return null when no keyword candidate matches.
  return null
}
